import {format} from 'date-fns';
import {CompanyRole, daysOfWeekLong} from '@/constants';
import {useTheme} from '@mui/material';
import {useMediaQuery} from '@mui/material';
import {EventData} from '@/types/event';
import {de, enUS} from 'date-fns/locale';
import i18n from 'i18next';

import {SupportedLanguage} from '@/types/common';

import {ignoreUser} from '@/services/matrix/user/ignore-user';
import {MatrixClient} from 'matrix-js-sdk';
import {fetchUserData} from '@/rest/user/fetch-user-data';
import {store} from '@/redux-store/store';
import {fetchOtherUserData} from '@/rest/user/fetch-other-user-data';
import {makeMatrixUserIdFromUserMail} from '@/components/matrix-chat/utils/helper-functions';
import keycloakAxios from '@/services/axios';
import {setUserBlacklist} from '@/redux-store/slices/auth';
import {unignoreUser} from '@/services/matrix/user/unignore-user';
import {GroupData} from '@/types/group';
import {CompanyInUserInfos} from '@/types/company';
import {UserInfosFormValues} from '@/redux-store/slices/user-infos';

const timeFormats: Record<SupportedLanguage, string> = {
  de: 'HH:mm',
  en: 'h:mm aa'
};

const locales: Record<SupportedLanguage, Locale> = {
  de: de,
  en: enUS
};

type Recurring = number[];

export const formatDate = (
  date: Date,
  recurring: Recurring,
  shortVersion = false
): string => {
  const defaultLanguage: SupportedLanguage = 'de';
  const currentLanguage: SupportedLanguage =
    (i18n.language as SupportedLanguage) || defaultLanguage;
  const locale = locales[currentLanguage];
  const timeFormat = timeFormats[currentLanguage];
  const recurringTextKey = shortVersion
    ? 'recurring_weekly_short'
    : 'recurring_weekly';
  const recurringText = i18n.t(recurringTextKey);
  const atTranslation = i18n.t('at');

  if (recurring.length > 0) {
    return `${recurringText}, ${format(date, 'ccc', {
      locale: locale
    })} ${atTranslation} ${format(date, timeFormat)}`;
  } else {
    const dateFormat = shortVersion ? 'PPp' : 'PPPp';
    return `${format(date, `ccc, ${dateFormat}`, {
      locale: locale
    })}`;
  }
};

export const formatRecurring = (recurring: Array<number>): string => {
  if (recurring.length === 1 && recurring[0] >= 1 && recurring[0] <= 7) {
    const dayOfWeekKey =
      daysOfWeekLong[recurring[0] as keyof typeof daysOfWeekLong];
    const dayOfWeek = i18n.t(dayOfWeekKey);
    const recurringText = i18n.t('recurring_weekly');
    return `${recurringText}, ${dayOfWeek}`;
  } else {
    return i18n.t('recurring_weekly');
  }
};

export const getButtonWidth = (
  buttonType: 'leave' | 'edit' | 'delete' | 'clone',
  selectedEventFromRedux?: EventData | null
): Record<string, string> => {
  const baseWidth = {
    xs: '65%',
    sm: '70%',
    md: '55%',
    lg: '50%'
  };

  const reducedWidth = {
    xs: '50%',
    sm: '85%',
    md: '60%',
    lg: '60%'
  };

  const isUserParticipant = !!selectedEventFromRedux;

  switch (buttonType) {
    case 'leave':
    case 'edit':
    case 'delete':
    case 'clone':
      return isUserParticipant ? reducedWidth : baseWidth;
    default:
      return baseWidth;
  }
};

export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export const useTitleStyle = (
  customMaxWidth = '28ch',
  customTitleWidth?: string,
  breakpoint?: Breakpoint,
  alwaysTruncate = false,
  breakpointUp?: Breakpoint
) => {
  const theme = useTheme();
  const isDown = useMediaQuery(theme.breakpoints.down(breakpoint || 'xl'));
  const isUp = useMediaQuery(theme.breakpoints.up(breakpointUp || 'xs'));

  const shouldTruncate =
    alwaysTruncate || (breakpoint && isDown) || (breakpointUp && isUp);

  const titleStyle = shouldTruncate
    ? {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: customTitleWidth || customMaxWidth
      }
    : {};

  return titleStyle;
};

export function getDistanceFromLatLonInKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in km
  return d;
}

function deg2rad(deg: number): number {
  return deg * (Math.PI / 180);
}

export const UserRoleTranslationKey: Record<CompanyRole, string> = {
  [CompanyRole.NONE]: '',
  [CompanyRole.EMPLOYEE_1]: 'employee',
  [CompanyRole.HEALTH_MANAGER]: 'company_manager',
  [CompanyRole.TRAINER]: 'trainer'
};

export function getTranslatedRole(
  t: (key: string) => string,
  role: CompanyRole
): string {
  const roleKey = UserRoleTranslationKey[role];
  return t(roleKey);
}

export function generateRandomString(length: number): string {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

export const blockUser = async (userId: string, matrixClient: MatrixClient) => {
  try {
    const data = await fetchOtherUserData(userId);
    const email = data.email;
    const matrixUserId = makeMatrixUserIdFromUserMail(email);

    await keycloakAxios.get(`user/block/${userId}`);

    try {
      const newUserValues = await fetchUserData();
      if (newUserValues) {
        store.dispatch(setUserBlacklist(newUserValues.userBlacklist));
      }
    } catch (error) {
      console.error('Error fetching updated user data:', error);
    }

    try {
      await ignoreUser(matrixClient, matrixUserId);
    } catch (error) {
      console.error('Error ignoring user in Matrix client:', error);
    }
  } catch (error) {
    console.error('Error blocking user or fetching user email:', error);
  }
};

export const unBlockUser = async (
  userId: string,
  matrixClient: MatrixClient
) => {
  try {
    const data = await fetchOtherUserData(userId);
    const email = data.email;
    const matrixUserId = makeMatrixUserIdFromUserMail(email);

    await keycloakAxios.get(`user/unblock/${userId}`);

    try {
      const newUserValues = await fetchUserData();
      if (newUserValues) {
        store.dispatch(setUserBlacklist(newUserValues.userBlacklist));
      }
    } catch (error) {
      console.error('Error fetching updated user data:', error);
    }

    try {
      await unignoreUser(matrixClient, matrixUserId);
    } catch (error) {
      console.error('Error unignoring user in Matrix client:', error);
    }
  } catch (error) {
    console.error('Error unblocking user or fetching user email:', error);
  }
};

export const beautyTextCutting = (
  text: string,
  maxLength: number,
  tolerance = 0,
  maxCut = 15
): string => {
  if (text.length <= maxLength + tolerance) {
    return text;
  }
  let cut = text.substring(0, maxLength + 1);
  while (cut.substring(cut.length - 1, cut.length) !== ' ' && --maxCut > 0) {
    cut = cut.substring(0, cut.length - 1);
  }
  return cut + '…';
};

export function formatPrice(price: number) {
  return price && price % 1 === 0 ? price.toString() : price.toFixed(2);
}

export function isHealthManager(role: string | undefined | null): boolean {
  return typeof role === 'string' && role === 'HEALTH_MANAGER';
}

export function isDocumentCompanyRoleHealthManager(
  document: EventData | GroupData
): boolean {
  return document.companyRole === 'HEALTH_MANAGER';
}

export function getCompanyLabel(
  companyId: string,
  userCompanies: CompanyInUserInfos[]
) {
  if (userCompanies && userCompanies.length > 0) {
    const label = userCompanies?.find((company) => company.id._id === companyId)
      ?.id.title;
    if (label) {
      return label;
    }
  }
}

export function getAppTag(
  email: string,
  pairs: Record<string, string>
): string | undefined {
  const trimmedEmail = email.trim();

  if (trimmedEmail in pairs) {
    return pairs[trimmedEmail];
  }

  return undefined;
}

export function isOneOfUserCompaniesDocument(
  document: EventData | GroupData,
  user: UserInfosFormValues
): boolean {
  const documentCompany = document.company;
  const userCompanies = user.companies;
  if (documentCompany && userCompanies && userCompanies.length > 0) {
    const userCompaniesIds = userCompanies.map((company) => company.id._id);
    return userCompaniesIds.includes(documentCompany);
  }
  return false;
}

export function isPublicCompanyDocument(
  document: EventData | GroupData | null,
  currentCompanyId: string | null,
  isPublicCompany: boolean
): boolean {
  if (document && isPublicCompany && currentCompanyId) {
    return document.company === currentCompanyId;
  }
  return false;
}

export function isUserDocumentManager(
  document: EventData | GroupData | null,
  user: UserInfosFormValues
): boolean {
  if (document) {
    return (
      document.managers &&
      document.managers.some((manager) => manager._id === user._id)
    );
  }
  return false;
}

export function decodeBase64ToObject(base64: string): Record<string, string> {
  try {
    const decoded = atob(base64);
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error parsing base64 JSON string:', error);
    return {};
  }
}

export function isAllowedTag(
  tag: string | undefined,
  tagPairsBase64: string
): boolean {
  if (!tag) {
    return false;
  }
  const tagPairs = decodeBase64ToObject(tagPairsBase64);
  return Object.values(tagPairs).includes(tag);
}

export function isPublicCompany(
  identifier: string,
  publicCompaniesBase64: string
): boolean {
  const publicCompanies = decodeBase64ToObject(publicCompaniesBase64);
  return Object.keys(publicCompanies).includes(identifier);
}

export function getPublicCompanyId(
  identifier: string,
  publicCompaniesBase64: string
): string {
  const publicCompanies = decodeBase64ToObject(publicCompaniesBase64);
  return publicCompanies[identifier];
}

export function getPublicCompanyTag(
  currentCompanyId: string,
  publicCompaniesBase64: string
): string | undefined {
  const publicCompanies = decodeBase64ToObject(publicCompaniesBase64);
  return Object.entries(publicCompanies).find(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([_, id]) => id === currentCompanyId
  )?.[0];
}
